import { generateUDTRequest } from '@services'

const interfaceName = 'modio'
const DEBUG = false

function _debugLog(fnName) {
  if (DEBUG) {
    console.log('Calling', fnName)

    if (!globalThis[interfaceName]) {
      console.warn('Missing global interface:', interfaceName)
    } else if (!globalThis[interfaceName][fnName]) {
      console.warn('Missing implementation for:', fnName)
    }
  }

  function log() {
    if (DEBUG) console.log(`${fnName}:`, ...arguments)
  }

  function err() {
    if (DEBUG) console.error(`${fnName}:`, ...arguments)
  }

  return {
    err,
    log,
  }
}

/**
 * Returns true if the interface is defined on the global object
 */
export function isDefined() {
  return !!globalThis[interfaceName]
}

/**
 * Get a UDT from SDK
 * @returns {Promise<string|null>} The UDT string or null if not available
 */
export async function getAuthToken() {
  const { log, err } = _debugLog('getAuthToken')
  try {
    let udt
    if (isDefined()) {
      udt = await globalThis[interfaceName]?.getAuthToken?.()
    }

    if (udt) {
      log('received UDT')
      return udt
    } else {
      log('no UDT received')
    }
  } catch (error) {
    err(error)
  }

  return null
}

/**
 * Generates a UDT and sends it to the SDK
 * @returns {Promise<void>?}
 */
export async function setAuthToken() {
  const { log, err } = _debugLog('setAuthToken')
  try {
    if (isDefined() || window?.chrome?.webview) {
      // Generate UDT from access token
      const { token } = await generateUDTRequest()
      let result
      if (isDefined()) {
        result = await globalThis[interfaceName]?.setAuthToken?.(token)
      } else if (window?.chrome?.webview) {
        window.chrome.webview.postMessage({
          setAuthToken: { token },
        })
      }
      log(result)
    }
  } catch (error) {
    err(error)
  }
}

/**
 * Set the logged in user's ID on the SDK
 * @param {string} userId The user's ID
 * @returns {Promise<void>?}
 */
export async function setUserId(userId) {
  const { log, err } = _debugLog('setUserId')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.setUserId?.(userId.toString())
    } else if (window?.chrome?.webview) {
      window.chrome.webview.postMessage({
        setUserId: { userId: userId.toString() },
      })
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Notify the SDK that a mod has been subscribed to or unsubscribed from
 * @param {number} modId The ID of the mod
 * @param {boolean} isSubscribed true if subscribed, false if unsubscribed
 * @returns {Promise<void>?}
 */
export async function notifySubscriptionChange(modId, isSubscribed) {
  const { log, err } = _debugLog('notifySubscriptionChange')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.notifySubscriptionChange?.(
        modId,
        isSubscribed
      )
    } else if (window?.chrome?.webview) {
      window.chrome.webview.postMessage({
        notifySubscriptionChange: { modId, isSubscribed },
      })
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Get the list of activated mod IDs from the SDK
 * @returns {Promise<number[]>?} An array of mod IDs
 */
export async function getActivations() {
  const { log, err } = _debugLog('getActivations')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.getActivations?.()
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Set the list of activated mod IDs on the SDK
 * @param {number[]} modIds An array of mod IDs
 * @returns {Promise<void>?}
 */
export async function setActivations(modIds) {
  const { log, err } = _debugLog('setActivations')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.setActivations?.(modIds)
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Notify the SDK that a mod has been activated or deactivated
 * @param {number} modId The ID of the mod
 * @param {boolean} isActivated true if activated, false if deactivated
 * @returns {Promise<void>?}
 */
export async function notifyActivationChange(modId, isActivated) {
  const { log, err } = _debugLog('notifyActivationChange')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.notifyActivationChange?.(
        modId,
        isActivated
      )
    } else if (window?.chrome?.webview) {
      window.chrome.webview.postMessage({
        notifyActivationChange: { modId, isActivated },
      })
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Get the list of locally installed mods from the SDK
 * @returns {Promise<LocalMod[]>?}
 */
export async function getLocalMods() {
  const { log, err } = _debugLog('getLocalMods')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.getLocalMods?.()
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Get the list of activated local mod IDs from the SDK
 * @returns {Promise<string[]>?} An array of mod IDs
 */
export async function getLocalActivations() {
  const { log, err } = _debugLog('getLocalActivations')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.getLocalActivations?.()
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Set the list of activated mod IDs on the SDK
 * @param {string[]} modIds An array of mod IDs
 * @returns {Promise<void>?}
 */
export async function setLocalActivations(modIds) {
  const { log, err } = _debugLog('setLocalActivations')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.setLocalActivations?.(modIds)
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Notify the SDK that a local mod has been activated or deactivated
 * @param {string | number} modId The ID of the mod
 * @param {boolean} isActivated true if activated, false if deactivated
 * @returns {Promise<void>?}
 */
export async function notifyLocalActivationChange(modId, isActivated) {
  const { log, err } = _debugLog('notifyLocalActivationChange')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.notifyLocalActivationChange?.(
        modId.toString(),
        isActivated
      )
    } else if (window?.chrome?.webview) {
      window.chrome.webview.postMessage({
        notifyLocalActivationChange: { modId, isActivated },
      })
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Get the language the SDK is using
 * @returns {Promise<string>?}
 */
export async function getLanguage() {
  const { log, err } = _debugLog('getLanguage')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.getLanguage?.()
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Get the platform the SDK is running on
 * @returns {Promise<string>?}
 */
export async function getPlatform() {
  const { log, err } = _debugLog('getPlatform')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.getPlatform?.()
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}

/**
 * Get the portal the SDK is authed with
 * @returns {Promise<string>?}
 */
export async function getPortal() {
  const { log, err } = _debugLog('getPortal')

  try {
    let result
    if (isDefined()) {
      result = await globalThis[interfaceName]?.getPortal?.()
    }
    log(result)
    return result
  } catch (error) {
    err(error)
  }
}
